<script setup>
  import { ref, computed, onMounted, watch, nextTick } from "vue";
  import { storeToRefs } from "pinia";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { useMasterReportsStore } from "@stores/masterReports/masterReports";
  import { useReportThemesStore } from "@stores/reportThemes/reportThemes";
  import { useTargetListStore } from "@stores/survey/reports/targetList";
  import { useSearchGroupListStore } from "@stores/survey/reports/searchGroupList";
  import { useQuestionListStore } from "@stores/survey/reports/questionList";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Switch } from "@/components/ui/switch";
  import { Select } from "@/components/episto-ui/select";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Loader2 } from "lucide-vue-next";

  const props = defineProps({
    mode: {
      type: String,
      default: "create",
      validator: (v) => ["create", "edit"].includes(v),
    },
  });

  const emit = defineEmits(["submit", "cancel"]);

  const surveyStore = useSurveyStore();
  const { areWeightsEnabled, token: surveyToken } = storeToRefs(surveyStore);

  const surveyTargetsStore = useSurveyTargetsStore();

  const surveyLocalesStore = useSurveyLocalesStore();
  const { localeOptions, defaultLocale } = storeToRefs(surveyLocalesStore);

  const reportsStore = useReportsV2Store();
  const { currentReport, saving } = storeToRefs(reportsStore);

  const masterReportsStore = useMasterReportsStore();
  const { availableMasterReports } = storeToRefs(masterReportsStore);

  const targetListStore = useTargetListStore();
  const { targetOptions } = storeToRefs(targetListStore);

  const searchGroupListStore = useSearchGroupListStore();
  const { groupOptions } = storeToRefs(searchGroupListStore);

  const questionListStore = useQuestionListStore();
  const { crosstabQuestionOptions } = storeToRefs(questionListStore);

  const reportThemesStore = useReportThemesStore();
  const { themes: reportThemes } = storeToRefs(reportThemesStore);

  const form = ref({
    name: "",
    targetId: 0, // 0 means "All targets"
    groupToken: null,
    locale: null, // Will be set from defaultLocale on mount
    masterReportId: null,
    reportThemeId: null,
    useWeightedData: true,
    crosstabType: "question",
    crosstabQuestionId: null,
    crosstabGroupTokens: [],
  });

  const filterRespondents = ref(false);
  const crosstabEnabled = ref(false);
  const originalForm = ref(null);

  const isCreating = computed(() => props.mode === "create");
  const isEditing = computed(() => props.mode === "edit");
  const formTitle = computed(() => (isCreating.value ? "New report" : "Report settings"));

  // Check if form has unsaved changes
  const hasChanges = computed(() => {
    if (!originalForm.value || isCreating.value) return true;
    return (
      form.value.name !== originalForm.value.name ||
      form.value.locale !== originalForm.value.locale ||
      form.value.useWeightedData !== originalForm.value.useWeightedData ||
      form.value.targetId !== originalForm.value.targetId ||
      form.value.groupToken !== originalForm.value.groupToken ||
      form.value.reportThemeId !== originalForm.value.reportThemeId ||
      form.value.crosstabQuestionId !== originalForm.value.crosstabQuestionId ||
      form.value.crosstabGroupTokens.join(",") !== originalForm.value.crosstabGroupTokens.join(",")
    );
  });

  function applyDefaultReportTheme(themes) {
    if (form.value.reportThemeId || !themes.length) return;
    const systemTheme = themes.find((t) => t.is_system);
    if (systemTheme) form.value.reportThemeId = systemTheme.id;
  }

  function snapshotForm() {
    return {
      name: form.value.name,
      locale: form.value.locale,
      useWeightedData: form.value.useWeightedData,
      targetId: form.value.targetId,
      groupToken: form.value.groupToken,
      reportThemeId: form.value.reportThemeId,
      crosstabQuestionId: form.value.crosstabQuestionId,
      crosstabGroupTokens: [...form.value.crosstabGroupTokens],
    };
  }

  // Track if component is initialized (to prevent watch from resetting on initial load)
  const isInitialized = ref(false);

  onMounted(async () => {
    // Selection is scoped by the survey server-side (the survey account's
    // templates/themes + system); access is gated by survey membership.
    await Promise.all([
      masterReportsStore.fetchAvailableMasterReports(surveyToken.value),
      reportThemesStore.fetchThemes(surveyToken.value),
      surveyTargetsStore.fetch(surveyToken.value),
      searchGroupListStore.fetchSearchGroups(),
      questionListStore.fetchQuestions(),
    ]);

    // Set default master report to first available
    if (isCreating.value && availableMasterReports.value.length > 0 && !form.value.masterReportId) {
      form.value.masterReportId = availableMasterReports.value[0].id;
    }

    applyDefaultReportTheme(reportThemes.value);

    // Set default locale from survey's default locale (only for create mode)
    if (isCreating.value && !form.value.locale) {
      form.value.locale = defaultLocale.value?.token || null;
    }

    // Mark as initialized after data is loaded (the watch with immediate:true handles initFormFromReport)
    await nextTick();
    isInitialized.value = true;
  });

  function initFormFromReport(report) {
    form.value.name = report.name || "";
    form.value.locale = report.locale || defaultLocale.value?.token || null;
    form.value.masterReportId = report.master_report_id || null;
    form.value.reportThemeId = report.report_theme_id || null;
    form.value.useWeightedData = report.use_weighted_data ?? true;

    // Set filters from report
    const targetId = report.global_filters?.target_id;
    const groupToken = report.global_filters?.group_token;

    form.value.targetId = targetId || 0;
    form.value.groupToken = groupToken || null;

    // Enable filter toggle if any filter is set
    const hasFilters = (targetId && targetId !== 0) || (groupToken && groupToken !== "");
    filterRespondents.value = hasFilters;

    const crosstabQuestionId = report.global_crosstab?.question_id || null;
    const crosstabGroupTokens = report.global_crosstab?.groups_tokens || [];

    form.value.crosstabQuestionId = crosstabQuestionId;
    form.value.crosstabGroupTokens = crosstabGroupTokens;
    form.value.crosstabType = crosstabGroupTokens.length > 0 ? "groups" : "question";
    crosstabEnabled.value = !!crosstabQuestionId || crosstabGroupTokens.length > 0;

    originalForm.value = snapshotForm();
  }

  watch(
    () => currentReport.value,
    (report) => {
      if (isEditing.value && report) {
        initFormFromReport(report);
      }
    },
    { immediate: true },
  );

  watch(availableMasterReports, (newMasterReports) => {
    if (isCreating.value && newMasterReports.length > 0 && !form.value.masterReportId) {
      form.value.masterReportId = newMasterReports[0].id;
    }
  });

  watch(reportThemes, (themes) => {
    applyDefaultReportTheme(themes);
  });

  // Reset filters when toggle is turned off by user (not on initial load)
  watch(filterRespondents, (value, oldValue) => {
    // Only reset if user explicitly turned off the toggle (not on initial load)
    if (isInitialized.value && !value && oldValue === true) {
      form.value.targetId = 0;
      form.value.groupToken = null;
    }
  });

  watch(crosstabEnabled, (value, oldValue) => {
    if (isInitialized.value && !value && oldValue === true) {
      form.value.crosstabType = "question";
      form.value.crosstabQuestionId = null;
      form.value.crosstabGroupTokens = [];
    }
  });

  const isFormValid = computed(() => {
    if (!form.value.name.trim()) return false;
    if (isCreating.value && !form.value.masterReportId) return false;
    if (!form.value.reportThemeId) return false;
    return true;
  });

  const masterReportOptions = computed(() =>
    availableMasterReports.value.map((t) => ({
      value: String(t.id),
      label: t.name,
      badge: t.is_qualitative ? "Qualitative" : undefined,
    })),
  );

  const reportThemeOptions = computed(() => {
    const sorted = [...reportThemes.value].sort((a, b) => (b.is_system ? 1 : 0) - (a.is_system ? 1 : 0));
    return sorted.map((t) => ({ value: String(t.id), label: t.is_system ? `${t.name} (default)` : t.name }));
  });

  const formattedTargetOptions = computed(() =>
    (targetOptions.value || []).map((t) => ({ value: String(t.value), label: t.label })),
  );

  const crosstabQuestionSelectOptions = computed(() =>
    crosstabQuestionOptions.value.map((q) => ({ value: String(q.value), label: q.label })),
  );

  const toggleCrosstabGroup = (groupToken) => {
    const tokens = form.value.crosstabGroupTokens;
    form.value.crosstabGroupTokens = tokens.includes(groupToken)
      ? tokens.filter((t) => t !== groupToken)
      : [...tokens, groupToken];
  };

  const dataModeOptions = [
    { value: "false", label: "Raw data" },
    { value: "true", label: "Weighted data" },
  ];

  async function onSubmit() {
    if (!isFormValid.value) return;

    const params = {
      name: form.value.name.trim(),
      locale: form.value.locale,
      use_weighted_data: form.value.useWeightedData,
      global_filters: {
        target_id: form.value.targetId === 0 ? null : form.value.targetId,
        group_token: form.value.groupToken,
      },
      global_crosstab: {
        question_id: form.value.crosstabType === "question" ? form.value.crosstabQuestionId : null,
        groups_tokens: form.value.crosstabType === "groups" ? form.value.crosstabGroupTokens : [],
      },
    };

    if (isCreating.value) {
      params.master_report_id = form.value.masterReportId;
    }
    params.report_theme_id = form.value.reportThemeId;

    emit("submit", params);

    if (isEditing.value) {
      originalForm.value = snapshotForm();
    }
  }
</script>

<template>
  <div class="flex justify-center">
    <div class="w-full max-w-2xl">
      <Card class="p-6">
        <CardHeader class="p-0 pb-6">
          <CardTitle class="heading-page">{{ formTitle }}</CardTitle>
        </CardHeader>
        <CardContent class="p-0">
          <form
            class="space-y-6"
            @submit.prevent="onSubmit">
            <!-- Report Name -->
            <div class="space-y-2">
              <Label for="name">Name <span class="text-destructive">*</span></Label>
              <div class="w-1/2">
                <Input
                  id="name"
                  v-model="form.name"
                  required />
              </div>
            </div>

            <!-- Master Report (create only) -->
            <div
              v-if="isCreating"
              class="space-y-2">
              <Label for="theme">Template <span class="text-destructive">*</span></Label>
              <div class="w-1/2">
                <Select
                  :model-value="form.masterReportId ? String(form.masterReportId) : null"
                  :options="masterReportOptions"
                  placeholder="Select a template"
                  @update:model-value="form.masterReportId = $event ? Number($event) : null" />
              </div>
            </div>

            <!-- Master Report (edit, read-only) -->
            <div
              v-if="isEditing && currentReport?.master_report"
              class="space-y-2">
              <Label>Template</Label>
              <div class="text-sm text-muted-foreground">
                {{ currentReport.master_report.name }}
              </div>
            </div>

            <!-- Report Theme (required) -->
            <div class="space-y-2">
              <Label>Theme <span class="text-destructive">*</span></Label>
              <div class="w-1/2">
                <Select
                  :model-value="form.reportThemeId ? String(form.reportThemeId) : null"
                  :options="reportThemeOptions"
                  placeholder="Select a theme..."
                  @update:model-value="form.reportThemeId = $event ? Number($event) : null" />
              </div>
            </div>

            <!-- Data Mode (only shown if weights are enabled) -->
            <div
              v-if="areWeightsEnabled"
              class="space-y-2">
              <Label for="data-mode">Raw / Weighted</Label>
              <div class="w-1/2">
                <Select
                  :model-value="String(form.useWeightedData)"
                  :options="dataModeOptions"
                  @update:model-value="form.useWeightedData = $event === 'true'" />
              </div>
            </div>

            <!-- Language -->
            <div class="space-y-2">
              <Label for="locale">Language</Label>
              <div class="w-1/2">
                <Select
                  v-model="form.locale"
                  :options="localeOptions"
                  placeholder="Select a language" />
              </div>
            </div>

            <!-- Filter Respondents Toggle -->
            <div class="space-y-4">
              <div class="flex items-center gap-2">
                <Switch
                  :model-value="filterRespondents"
                  @update:model-value="filterRespondents = $event" />
                <Label
                  class="cursor-pointer"
                  @click="filterRespondents = !filterRespondents">
                  Filter respondents
                </Label>
              </div>

              <!-- Target and Group Filters (shown when toggle is on) -->
              <template v-if="filterRespondents">
                <div class="space-y-2">
                  <Label for="target">Target</Label>
                  <div class="w-1/2">
                    <Select
                      :model-value="form.targetId !== null ? String(form.targetId) : null"
                      :options="formattedTargetOptions"
                      placeholder="All respondents"
                      @update:model-value="form.targetId = $event ? Number($event) : 0" />
                  </div>
                </div>

                <div class="space-y-2">
                  <Label for="group">Group</Label>
                  <div class="w-1/2">
                    <Select
                      v-model="form.groupToken"
                      :options="groupOptions"
                      placeholder="All groups" />
                  </div>
                </div>
              </template>
            </div>

            <!-- Cross-tabulation Toggle -->
            <div class="space-y-4">
              <div class="flex items-center gap-2">
                <Switch
                  :model-value="crosstabEnabled"
                  @update:model-value="crosstabEnabled = $event" />
                <Label
                  class="cursor-pointer"
                  @click="crosstabEnabled = !crosstabEnabled">
                  Cross-tabulation
                </Label>
              </div>

              <template v-if="crosstabEnabled">
                <div class="flex gap-4">
                  <label class="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      v-model="form.crosstabType"
                      type="radio"
                      name="report-crosstab-type"
                      value="question"
                      class="text-primary" />
                    Question
                  </label>
                  <label class="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      v-model="form.crosstabType"
                      type="radio"
                      name="report-crosstab-type"
                      value="groups"
                      class="text-primary" />
                    Saved groups
                  </label>
                </div>

                <div
                  v-if="form.crosstabType === 'question'"
                  class="w-1/2">
                  <Select
                    :model-value="form.crosstabQuestionId ? String(form.crosstabQuestionId) : null"
                    :options="crosstabQuestionSelectOptions"
                    placeholder="Select a question"
                    @update:model-value="form.crosstabQuestionId = $event ? Number($event) : null" />
                </div>

                <div
                  v-if="form.crosstabType === 'groups'"
                  class="w-1/2">
                  <Popover>
                    <PopoverTrigger as-child>
                      <button
                        type="button"
                        class="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                        <span :class="form.crosstabGroupTokens.length === 0 ? 'text-muted-foreground' : ''">
                          {{
                            form.crosstabGroupTokens.length === 0
                              ? "Select groups..."
                              : `${form.crosstabGroupTokens.length} group(s) selected`
                          }}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      class="w-[200px] p-2"
                      align="start">
                      <div class="space-y-1 max-h-[200px] overflow-y-auto">
                        <div
                          v-for="g in groupOptions"
                          :key="g.value"
                          class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                          @click.prevent.stop="toggleCrosstabGroup(g.value)">
                          <Checkbox
                            :model-value="form.crosstabGroupTokens.includes(g.value)"
                            class="pointer-events-none" />
                          <span class="text-sm">{{ g.label }}</span>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </template>
            </div>

            <!-- Submit Button -->
            <div class="flex gap-3 pt-4">
              <Button
                type="submit"
                class="flex-1"
                :variant="isEditing && !hasChanges ? 'outline' : 'default'"
                :disabled="!isFormValid || saving || (isEditing && !hasChanges)">
                <Loader2
                  v-if="saving"
                  class="w-4 h-4 mr-2 animate-spin" />
                {{ isCreating ? "Create" : "Save" }}
              </Button>
              <Button
                v-if="isCreating"
                type="button"
                variant="outline"
                class="flex-1"
                @click="emit('cancel')">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
